import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { NotificationSettings } from '../types';
import { applySettings } from '../utils/formatMoney';

interface FullSettings extends NotificationSettings {
  system: {
    timezone: string;
    language: string;
    companyName: string;
    address: string;
    phone: string;
    website: string;
    currency: string;
    vatRate: number;
    dateFormat: string;
    timeFormat: '24h' | '12h';
    theme: 'light' | 'dark';
  };
  booking: {
    maxAdvanceDays: number;
    minCancellationHours: number;
  };
  payment: {
    stripeEnabled: boolean;
    stripePublicKey: string;
    stripeSecretKey: string;
    twintEnabled: boolean;
    twintApiKey: string;
    cashEnabled: boolean;
  };
}

const defaultSettings: FullSettings = {
  email: {
    enabled: true,
    confirmationEnabled: true,
    reminderEnabled: true,
    cancelationEnabled: true,
    reminder24h: true,
    reminder2h: true,
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    senderEmail: '',
    senderName: '',
    emailjsEnabled: false,
    emailjsServiceId: '',
    emailjsTemplateId: '',
    emailjsUserId: ''
  },
  sms: {
    enabled: false,
    confirmationEnabled: false,
    reminderEnabled: false,
    cancelationEnabled: false,
    reminder24h: false,
    reminder2h: false,
    provider: 'twilio',
    apiKey: '',
    apiSecret: '',
    senderNumber: ''
  },
  system: {
    timezone: 'Europe/Zurich',
    language: 'de',
    companyName: '',
    address: '',
    phone: '',
    website: '',
    currency: 'CHF',
    vatRate: 0,
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    theme: 'light'
  },
  booking: {
    maxAdvanceDays: 30,
    minCancellationHours: 24
  },
  payment: {
    stripeEnabled: false,
    stripePublicKey: '',
    stripeSecretKey: '',
    twintEnabled: false,
    twintApiKey: '',
    cashEnabled: true
  }
};

export const useNotificationSettings = () => {
  const [settings, setSettings] = useState<FullSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('key', 'notification')
        .single();
      if (error && error.code !== 'PGRST116') throw error; // ignore not found
      if (data) {
        const merged = { ...defaultSettings, ...(data.value as any) } as FullSettings;
        setSettings(merged);
        // Alle system-Einstellungen sofort global anwenden
        if (merged.system) applySettings(merged.system);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: FullSettings) => {
    try {
      setLoading(true);
      const { error } = await supabase.from('settings').upsert([
        { key: 'notification', value: newSettings }
      ], { onConflict: 'key' });
      if (error) throw error;
      setSettings(newSettings);
      if (newSettings.system) applySettings(newSettings.system);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return { settings, loading, error, saveSettings, fetchSettings, setSettings };
}; 